import { makeAutoObservable, runInAction } from 'mobx';
import ApiStore from './ApiStore';
import { ExtendedEvent } from '../pages/Extended Calendar/types';

class ProjectEventStore {
  isLoading = false;
  events: ExtendedEvent[] = [];
  private api: ApiStore;

  constructor(apiStore: ApiStore) {
    makeAutoObservable(this);
    this.api = apiStore;
  }

  /* ─────── helpers ─────── */
  private setEvents = (list: ExtendedEvent[]) => {
    runInAction(() => (this.events = list));
  };

  private setLoading = (v: boolean) =>
    runInAction(() => (this.isLoading = v));

  /* ─────── public API ─────── */
  getAll = async () => {
    this.setLoading(true);
    const list = await this.api.ProjectEventsApiClient.getAll();
    this.setEvents(list);
    this.setLoading(false);
    return list;
  };

  addEvents = async (events: Omit<ExtendedEvent, 'id'>[]) => {
    if (!events.length) return;
    this.setLoading(true);

    await this.api.ProjectEventsApiClient.addEvents(
      events.map(e => ({ ...e, id: undefined } as any)),
    );
    await this.getAll();
    this.setLoading(false);
  };

  updateOccurrence = async (masterId: string | number, dto: any) => {
    this.setLoading(true);
    await this.api.ProjectEventsApiClient.updateOccurrence(masterId, dto);
    await this.getAll();
    this.setLoading(false);
  };

  updateSeries = async (id: string | number, dto: any) => {
    this.setLoading(true);
    await this.api.ProjectEventsApiClient.updateSeries(id, dto);
    await this.getAll();
    this.setLoading(false);
  };

  /**
   * Delete a concrete *stored* entity by id.  
   * (Used for masters and overrides that exist in DB.)
   */
  deleteById = async (
    id: string,
    isOccurrence = false,
    masterId?: string,
  ) => {
    this.setLoading(true);
    try {
      if (isOccurrence && masterId) {
        const numericId = Number(String(id).replace(/^project_/, ''));
        if (!Number.isNaN(numericId)) {
          await this.api.ProjectEventsApiClient.deleteEvent(
            numericId,
            true,
            masterId,
          );
        }
      } else {
        await this.api.ProjectEventsApiClient.deleteEvent(id);
      }
      await this.getAll();
    } finally {
      this.setLoading(false);
    }
  };

  /**
   * Delete an *auto-expanded* calendar instance (it has no own ID in DB).
   */
  deleteGeneratedOccurrence = async (event: ExtendedEvent) => {
    if (!event.parentEventId) return; // sanity check

    this.setLoading(true);
    try {
      await this.api.ProjectEventsApiClient.tombstoneOccurrence(
        event.parentEventId,
        {
          startDate: event.start,
          endDate: event.end,
          title: event.title,
          isDeleted: true,
        },
      );
      await this.getAll();
    } finally {
      this.setLoading(false);
    }
  };

  approveAttendance = async (id: string | number) => {
    await this.api.ProjectEventsApiClient.approve(id);
    await this.getAll();
  };
}

export default ProjectEventStore;
